import { memo } from "react";
import { Clock, User, Flame } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { LeadCounts } from "@/hooks/useDashboardData";

interface LeadCardsProps {
  globalLeadCounts: LeadCounts;
  isLoadingLeadCounts: boolean;
  selectedLeadType?: 'hot' | 'followup' | 'na' | null;
  onLeadCardClick: (type: 'hot' | 'followup' | 'na') => void;
}

export const LeadCards = memo(function LeadCards({
  globalLeadCounts,
  isLoadingLeadCounts,
  selectedLeadType,
  onLeadCardClick
}: LeadCardsProps) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <button 
        onClick={() => onLeadCardClick('hot')}
        disabled={isLoadingLeadCounts}
        className={cn(
          "cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] bg-card border rounded-lg",
          selectedLeadType === 'hot' 
            ? "bg-red-50 border-red-200 ring-2 ring-red-200 shadow-md" 
            : "hover:bg-red-50 hover:border-red-200",
          isLoadingLeadCounts && "pointer-events-none"
        )}
      >
        <div className="p-3">
          <div className="flex flex-col items-center text-center gap-1">
            <Flame className={cn("h-5 w-5", selectedLeadType === 'hot' ? "text-red-600" : "text-red-500")} />
            <div>
              {isLoadingLeadCounts ? (
                <Skeleton className="h-6 w-8 mx-auto mb-1" />
              ) : (
                <p className={cn("text-lg font-bold", selectedLeadType === 'hot' ? "text-red-700" : "text-red-600")}>
                  {globalLeadCounts.hot}
                </p>
              )}
              <p className="text-xs font-medium text-muted-foreground">Hot Leads</p>
            </div>
          </div>
        </div>
      </button>
      
      <button 
        onClick={() => onLeadCardClick('followup')}
        disabled={isLoadingLeadCounts}
        className={cn(
          "cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] bg-card border rounded-lg",
          selectedLeadType === 'followup' 
            ? "bg-yellow-50 border-yellow-200 ring-2 ring-yellow-200 shadow-md" 
            : "hover:bg-yellow-50 hover:border-yellow-200",
          isLoadingLeadCounts && "pointer-events-none"
        )}
      >
        <div className="p-3">
          <div className="flex flex-col items-center text-center gap-1">
            <Clock className={cn("h-5 w-5", selectedLeadType === 'followup' ? "text-yellow-600" : "text-yellow-500")} />
            <div>
              {isLoadingLeadCounts ? (
                <Skeleton className="h-6 w-8 mx-auto mb-1" />
              ) : (
                <p className={cn("text-lg font-bold", selectedLeadType === 'followup' ? "text-yellow-700" : "text-yellow-600")}>
                  {globalLeadCounts.followup}
                </p>
              )}
              <p className="text-xs font-medium text-muted-foreground">Follow-ups</p>
            </div>
          </div>
        </div>
      </button>
      
      <button 
        onClick={() => onLeadCardClick('na')}
        disabled={isLoadingLeadCounts}
        className={cn(
          "cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] bg-card border rounded-lg",
          selectedLeadType === 'na' 
            ? "bg-gray-50 border-gray-200 ring-2 ring-gray-200 shadow-md" 
            : "hover:bg-gray-50 hover:border-gray-200",
          isLoadingLeadCounts && "pointer-events-none"
        )}
      >
        <div className="p-3">
          <div className="flex flex-col items-center text-center gap-1">
            <User className={cn("h-5 w-5", selectedLeadType === 'na' ? "text-gray-600" : "text-gray-500")} />
            <div>
              {isLoadingLeadCounts ? (
                <Skeleton className="h-6 w-8 mx-auto mb-1" />
              ) : (
                <p className={cn("text-lg font-bold", selectedLeadType === 'na' ? "text-gray-700" : "text-gray-600")}>
                  {globalLeadCounts.na}
                </p>
              )}
              <p className="text-xs font-medium text-muted-foreground">Nicht Angetroffen</p>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
});
